import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  BankAccountAnswer,
  BankAccountType,
  OnboardingAnswers,
  createEmptyBankAccount
} from '../../utils/onboardingCategoryBuilder';
import { BankCountrySelect } from './OnboardingBuilderFields';
import BankDropdown from '../BankDropdown';
import { fetchBanksByCountry } from '../../utils/banksApi';
import { getCountryDefaultCurrency, getDefaultBankCountry } from '../../utils/countries';

const ACCOUNT_TYPE_KEYS: BankAccountType[] = [
  'checking',
  'savings',
  'creditCard',
  'investment',
  'loan',
  'cash',
  'other'
];

interface OnboardingAccountsBuilderProps {
  answers: OnboardingAnswers;
  onChange: (answers: OnboardingAnswers) => void;
}

const CardRemoveButton: React.FC<{ onClick: () => void; label: string }> = ({
  onClick,
  label
}) => (
  <button type="button" className="builder-remove" onClick={onClick} aria-label={label}>
    ×
  </button>
);

const AccountBankSelect: React.FC<{
  country: string;
  bankId: string;
  onBankSelect: (bankId: string, bankName: string) => void;
}> = ({ country, bankId, onBankSelect }) => {
  const { t, i18n } = useTranslation();
  const {
    data: banks = [],
    isLoading,
    error
  } = useQuery({
    queryKey: ['banks', country, i18n.language],
    queryFn: () => fetchBanksByCountry(country, i18n.language),
    enabled: Boolean(country),
    staleTime: 5 * 60 * 1000
  });

  if (!country) {
    return null;
  }

  const handleBankSelect = (selectedBankId: string) => {
    if (selectedBankId === 'other') {
      onBankSelect('other', t('otherBanks', { ns: 'bankDropdown' }));
      return;
    }
    const bank = banks.find(b => b.id === selectedBankId);
    onBankSelect(selectedBankId, bank?.name ?? selectedBankId);
  };

  return (
    <label className="builder-field onboarding-bank-field">
      <span>{t('selectBank', { ns: 'tools' })}</span>
      {isLoading ? (
        <div className="onboarding-bank-loading">
          <div className="spinner" />
          <p>{t('loadingBanks', { ns: 'tools' })}</p>
        </div>
      ) : error ? (
        <div className="onboarding-bank-error">
          <p>
            {t('error', { ns: 'tools' })}: {error.message}
          </p>
        </div>
      ) : (
        <BankDropdown
          banks={banks}
          selectedBank={bankId || null}
          onBankSelect={handleBankSelect}
          placeholder={t('chooseBank', { ns: 'tools' })}
        />
      )}
    </label>
  );
};

const OnboardingAccountsBuilder: React.FC<OnboardingAccountsBuilderProps> = ({
  answers,
  onChange
}) => {
  const { t } = useTranslation();
  const defaultCurrency = answers.primaryCurrency || 'USD';
  const defaultCountry = getDefaultBankCountry(answers.primaryCountry || '');

  const update = (patch: Partial<OnboardingAnswers>) => {
    onChange({ ...answers, ...patch });
  };

  const updateAccount = (index: number, patch: Partial<BankAccountAnswer>) => {
    const bankAccounts = [...answers.bankAccounts];
    bankAccounts[index] = { ...bankAccounts[index], ...patch };
    update({ bankAccounts });
  };

  const handleCountryChange = (index: number, country: string) => {
    updateAccount(index, { country, bankId: '', bankName: '' });
  };

  const handleBankSelect = (index: number, bankId: string, bankName: string) => {
    const account = accounts[index];
    const patch: Partial<BankAccountAnswer> = { bankId, bankName };
    if (!account.name.trim()) {
      const currency = getCountryDefaultCurrency(account.country || defaultCountry);
      patch.name = `${bankName} — ${currency}`;
    }
    updateAccount(index, patch);
  };

  const accounts =
    answers.bankAccounts.length > 0
      ? answers.bankAccounts
      : [createEmptyBankAccount(0, defaultCountry, defaultCurrency)];

  return (
    <div className="onboarding-accounts-builder">
      {accounts.map((account, index) => (
        <div key={account.id} className="builder-card onboarding-account-card">
          <div className="builder-card-header">
            <span className="onboarding-account-card-title">
              {t('steps.bankAccounts.accountNumber', {
                ns: 'onboarding',
                number: index + 1
              })}
            </span>
            {accounts.length > 1 && (
              <CardRemoveButton
                onClick={() =>
                  update({
                    bankAccounts: accounts.filter((_, i) => i !== index)
                  })
                }
                label={t('builder.actions.remove', { ns: 'onboarding' })}
              />
            )}
          </div>

          <BankCountrySelect
            label={t('builder.fields.country', { ns: 'onboarding' })}
            value={account.country}
            onChange={country => handleCountryChange(index, country)}
            required
          />

          <AccountBankSelect
            country={account.country}
            bankId={account.bankId || ''}
            onBankSelect={(bankId, bankName) => handleBankSelect(index, bankId, bankName)}
          />

          <label className="builder-field">
            <span>{t('steps.bankAccounts.typeLabel', { ns: 'onboarding' })}</span>
            <select
              value={account.accountType || 'checking'}
              onChange={e =>
                updateAccount(index, { accountType: e.target.value as BankAccountType })
              }
            >
              {ACCOUNT_TYPE_KEYS.map(typeKey => (
                <option key={typeKey} value={typeKey}>
                  {t(`steps.bankAccounts.types.${typeKey}`, { ns: 'onboarding' })}
                </option>
              ))}
            </select>
          </label>

          <label className="builder-field">
            <span>{t('steps.bankAccounts.nameLabel', { ns: 'onboarding' })}</span>
            <input
              type="text"
              className="builder-text-input"
              value={account.name}
              placeholder={t('steps.bankAccounts.namePlaceholder', { ns: 'onboarding' })}
              onChange={e => updateAccount(index, { name: e.target.value })}
            />
          </label>
        </div>
      ))}

      <button
        type="button"
        className="builder-add"
        onClick={() =>
          update({
            bankAccounts: [
              ...accounts,
              createEmptyBankAccount(accounts.length, defaultCountry, defaultCurrency)
            ]
          })
        }
      >
        + {t('steps.bankAccounts.add', { ns: 'onboarding' })}
      </button>
    </div>
  );
};

export default OnboardingAccountsBuilder;
